import { FC, memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormLabel,
  Grid,
  GridItem,
  Switch,
  Tab,
  TabList,
  Tabs,
  Tag,
  TagCloseButton,
  TagLabel,
} from '@chakra-ui/react';
import { ContactParticipant, accountApi } from '@netiva/classifieds-api';
import { skipToken } from '@reduxjs/toolkit/query';

import { ConversationList, MessageThread } from './components';
import { useAppSelector } from '@/store';

import { useMessagesSearchParams } from './hooks';

const PageSize = 20;

/** Tab order, and the side of a conversation each one shows. */
const ParticipantTabs: ContactParticipant[] = ['Advertiser', 'Contact'];

export const MessagesPage: FC = memo(function MessagesPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useMessagesSearchParams();
  const { advertId, conversationId, pageIndex, participant, unreadOnly } = searchParams;

  const { unreadAdvertiserMessageCount, unreadContactMessageCount } = useAppSelector((state) => state.account);

  // The tab is the side, so the list always asks for one - the URL simply omits the first.
  const activeParticipant = participant ?? ParticipantTabs[0];
  const tabIndex = Math.max(ParticipantTabs.indexOf(activeParticipant), 0);

  const { data: conversations, isFetching: isLoadingList } = accountApi.useGetContactConversations({
    dataObjectId: advertId,
    participant: activeParticipant,
    unreadOnly: unreadOnly || undefined,
    pageIndex,
    pageSize: PageSize,
  });

  // currentData, not data: RTK Query keeps the previous argument's result in `data` across an
  // argument change, which would leave the last conversation on screen after it was deselected.
  const { currentData: conversation, isFetching: isLoadingThread } = accountApi.useGetContactConversation(
    conversationId ? { id: conversationId } : skipToken
  );

  const [reply, { isLoading: isSending }] = accountApi.useReplyToContactMessage();
  const [markRead] = accountApi.useMarkContactMessagesRead();
  const [markUnread, { isLoading: isMarkingUnread }] = accountApi.useMarkContactConversationUnread();

  // Selecting a conversation is what marks it read - the GET has no side effect of its own. Keyed on
  // the ID so it also covers arriving on a link that names one, and so that putting a conversation
  // back to unread is not immediately undone: that leaves the selection untouched.
  useEffect(() => {
    if (!conversationId) return;
    markRead({ accountContactMessagesMarkReadRequest: { conversationIds: [conversationId] } });
  }, [conversationId, markRead]);

  const handleSelect = useCallback(
    (id: number) => setSearchParams({ ...searchParams, conversationId: id }),
    [searchParams, setSearchParams]
  );

  // A conversation opened from a link - a migrated short link, or the one in a reply mail - can be on
  // either side, so the tab follows what actually came back rather than stranding it behind the
  // other tab with a list that does not contain it.
  useEffect(() => {
    if (!conversation || conversation.participant === activeParticipant) return;
    setSearchParams({ ...searchParams, participant: conversation.participant }, true);
  }, [activeParticipant, conversation, searchParams, setSearchParams]);

  const handleTabChange = useCallback(
    (index: number) =>
      setSearchParams({
        ...searchParams,
        conversationId: undefined,
        pageIndex: 0,
        participant: ParticipantTabs[index],
      }),
    [searchParams, setSearchParams]
  );

  const handleClearAdvertFilter = useCallback(
    () => setSearchParams({ ...searchParams, advertId: undefined, pageIndex: 0 }),
    [searchParams, setSearchParams]
  );

  const handleUnreadOnlyChange = useCallback(
    (nextUnreadOnly: boolean) =>
      setSearchParams({
        ...searchParams,
        pageIndex: 0,
        unreadOnly: nextUnreadOnly,
        // Opening a conversation is what made it read, so filtering to unread is precisely what hides it.
        // Leaving it open would show a thread the list no longer offers.
        conversationId: nextUnreadOnly ? undefined : searchParams.conversationId,
      }),
    [searchParams, setSearchParams]
  );

  const handleReply = useCallback(
    async (content: string) => {
      if (!conversationId) return;
      await reply({ id: conversationId, accountContactMessagesReplyRequest: { content } }).unwrap();
    },
    [conversationId, reply]
  );

  const handleMarkUnread = useCallback(async () => {
    if (!conversationId) return;
    await markUnread({ id: conversationId }).unwrap();
  }, [conversationId, markUnread]);

  const handleMarkAllRead = useCallback(async () => {
    // no IDs means every conversation of the account
    await markRead({ accountContactMessagesMarkReadRequest: {} }).unwrap();
  }, [markRead]);

  const totalPages = Math.ceil((conversations?.totalCount || 0) / PageSize);

  // Arriving from an advert only shows that advert's conversations, which looks like an empty or
  // oddly short inbox unless the filter says so. The title comes from the rows themselves - falling
  // back to the ID, because a filter that matches nothing has no row to read it from.
  const advertFilterLabel = advertId
    ? conversations?.items.find((item) => item.dataObjectId === advertId)?.dataObjectTitle || `#${advertId}`
    : undefined;

  const tabs = [
    { label: t('messages.tabs.incoming'), unreadCount: unreadAdvertiserMessageCount },
    { label: t('messages.tabs.sent'), unreadCount: unreadContactMessageCount },
  ];

  return (
    <Flex direction="column" gap={4} height="full">
      <Tabs isLazy size="lg" index={tabIndex} onChange={handleTabChange}>
        <TabList>
          {tabs.map((tab) => (
            <Tab key={tab.label}>
              {tab.label}
              {tab.unreadCount > 0 && (
                <Badge fontSize="md" ml={2}>
                  {tab.unreadCount}
                </Badge>
              )}
            </Tab>
          ))}
        </TabList>
      </Tabs>

      <Flex justify="flex-end" align="center" gap={4} wrap="wrap">
        {advertFilterLabel && (
          <Tag size="lg" colorScheme="primary" borderRadius="full" mr="auto">
            <TagLabel>{t('messages.filter.advert', { advert: advertFilterLabel })}</TagLabel>
            <TagCloseButton onClick={handleClearAdvertFilter} aria-label={t('messages.filter.clear')} />
          </Tag>
        )}

        <Flex align="center" gap={4}>
          <Flex align="center" gap={2}>
            <Switch id="unreadOnly" isChecked={unreadOnly} onChange={(e) => handleUnreadOnlyChange(e.target.checked)} />
            <FormLabel htmlFor="unreadOnly" mb={0} fontWeight="normal">
              {t('messages.list.unreadOnly')}
            </FormLabel>
          </Flex>

          <Button size="sm" variant="outline" onClick={handleMarkAllRead}>
            {t('messages.list.markAllRead')}
          </Button>
        </Flex>
      </Flex>

      <Grid
        templateColumns={{ base: '1fr', lg: '280px 1fr', xl: '320px 1fr' }}
        borderWidth="1px"
        borderColor="neutral.200"
        borderRadius="md"
        overflow="hidden"
        flex="1"
        minH="60vh"
      >
        {/* On mobile the thread replaces the list, so only one of the two is mounted. */}
        <GridItem
          borderRightWidth={{ base: 0, lg: '1px' }}
          borderColor="neutral.200"
          overflowY="auto"
          display={{ base: conversationId ? 'none' : 'block', lg: 'block' }}
          opacity={isLoadingList ? 0.6 : 1}
        >
          <ConversationList
            conversations={conversations?.items || []}
            selectedId={conversationId}
            totalPages={totalPages}
            onSelect={handleSelect}
          />
        </GridItem>

        {/* A flex column so the back button takes its own height and the thread gets the rest -
            as a plain block the thread's height:100% would overflow the card and clip the reply. */}
        <GridItem
          display={{ base: conversationId ? 'flex' : 'none', lg: 'flex' }}
          flexDirection="column"
          minW={0}
          minH={0}
        >
          <Box display={{ base: 'block', lg: 'none' }} p={2}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSearchParams({ ...searchParams, conversationId: undefined })}
            >
              {t('messages.thread.back')}
            </Button>
          </Box>

          <MessageThread
            conversation={conversation}
            hasSelection={!!conversationId}
            isLoading={isLoadingThread}
            isMarkingUnread={isMarkingUnread}
            isSending={isSending}
            onMarkUnread={handleMarkUnread}
            onReply={handleReply}
          />
        </GridItem>
      </Grid>
    </Flex>
  );
});
